import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_LOCALE, DateAdapter } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { AlertCardComponent } from '../../../shared/components/alert-card/alert-card.component';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { NaturezaMovimentacaoEnum } from '../../../shared/enums/financas/natureza-movimentacao.enum';
import { TipoContaEnum } from '../../../shared/enums/financas/tipo-conta.enum';
import { TipoMatchConciliacaoEnum } from '../../../shared/enums/financas/tipo-match-conciliacao.enum';
import { ModoConciliacaoCartaoEnum } from '../../../shared/enums/financas/modo-conciliacao-cartao.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { CustomDateAdapter } from '../../../shared/services/custom-date-adapter';
import { ConciliacaoCartaoCreditoService } from '../../../shared/services/apis/financas/conciliacao-cartao-credito.service';
import { ConciliacaoBancariaService } from '../../../shared/services/apis/financas/conciliacao-bancaria.service';
import { StatusConexaoEnum } from '../../../shared/enums/integracao-financas-banco/status-conexao.enum';
import { FinancasLookupService } from '../../../shared/services/apis/financas/financas-lookup.service';
import { FinancasLookupItem } from '../../../shared/services/responses/financas/lookup-online-select.response';
import { PessoaLookupService } from '../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { LookupItemStruct } from '../../../shared/services/structs/pessoa/lookup-item.struct';
import { ExtratoBancarioContaItem } from '../../../shared/services/responses/financas/extrato-bancario-contas.response';
import { ExtratoBancarioItemStruct as ExtratoBancarioItem } from '../../../shared/services/structs/financas/extrato-bancario-item.struct';
import { SugestaoMatchStruct as SugestaoMatch } from '../../../shared/services/structs/financas/sugestao-match.struct';
import { ConciliarCartaoCreditoRequest } from '../../../shared/services/requests/financas/conciliar-cartao.request';
import {
    ConciliarCartaoDialogComponent,
    ConciliarCartaoDialogData,
} from './dialogs/conciliar-cartao-dialog/conciliar-cartao-dialog.component';
import { ExtratoService } from '../../../shared/services/apis/integracao-financas-banco/extrato.service';

export enum PeriodoConciliacaoEnum {
    Hoje = 1,
    DaSemana = 2,
    DoMes = 3,
    Ultimos30Dias = 4,
    DoIntervalo = 5,
}

interface LancamentoCartaoVm {
    item: ExtratoBancarioItem;
    sugestaoIdx: number;
    isConciliando: boolean;
    isIgnorando: boolean;
    acaoAberta: 'novaCp' | 'novaMov' | null;
    formNovaCp: { idPessoaFornecedor: number | null; idCategoriaFinanceira: number | null; descricao: string; };
    formNovaMov: { idCategoriaFinanceira: number | null; idPessoa: number | null; descricao: string; };
}

@Component({
    selector: 'app-conciliacao-cartao-lancamentos',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        HeaderComponent,
        EmptyListComponent,
        LoadingBoxComponent,
        AlertCardComponent,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSelectModule,
        MatDialogModule,
        MatMenuModule,
        MatProgressSpinnerModule,
    ],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
        { provide: DateAdapter, useClass: CustomDateAdapter },
    ],
    templateUrl: './conciliacao-cartao-lancamentos.component.html',
    styleUrls: ['./conciliacao-cartao-lancamentos.component.css'],
})
export class ConciliacaoCartaoLancamentosComponent implements OnInit {
    public moduloEnum = ModuloEnum.Financas;
    public funcionalidadeEnum = FuncionalidadeEnum.Financas_ConciliacaoBancaria;
    public naturezaEnum = NaturezaMovimentacaoEnum;
    public PeriodoConciliacaoEnum = PeriodoConciliacaoEnum;

    public idContaFinanceira!: number;
    public descricaoContaFinanceira = '';
    public nomeBanco = '';
    public agencia = '';
    public numeroConta = '';

    // ── Contas ────────────────────────────────────────────────────────────
    public _contas: ExtratoBancarioContaItem[] = [];
    public isLoadingContas = false;
    public statusConexaoExtrato: StatusConexaoEnum | null = null;
    public statusConexaoEnum = StatusConexaoEnum;

    // ── Lista + paginação ─────────────────────────────────────────────────
    public isLoading = false;
    public isLoadingMais = false;
    private _todosVm: LancamentoCartaoVm[] = [];
    public _filtradosVm: LancamentoCartaoVm[] = [];
    private _paginaAtual = 0;
    public _temMais = false;
    public filtroNatureza: number | null = null;

    // ── Período ───────────────────────────────────────────────────────────
    public periodoSelecionado: PeriodoConciliacaoEnum = PeriodoConciliacaoEnum.Ultimos30Dias;
    public model!: FormGroup;
    private _periodoAnterior: PeriodoConciliacaoEnum = PeriodoConciliacaoEnum.Ultimos30Dias;
    public alertForaPeriodo: { quantidade: number; mesLabel: string; dataDe: Date; dataAte: Date } | null = null;

    // ── Lookups ───────────────────────────────────────────────────────────
    public fornecedoresLookup: LookupItemStruct[] = [];
    public categoriasLookup: FinancasLookupItem[] = [];

    @ViewChild('periodoMenuTrigger') periodoMenuTrigger!: MatMenuTrigger;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
        private fb: FormBuilder,
        private alertService: AlertService,
        private cartaoService: ConciliacaoCartaoCreditoService,
        private bancarioService: ConciliacaoBancariaService,
        private extratoService: ExtratoService,
        private financasLookupService: FinancasLookupService,
        private pessoaLookupService: PessoaLookupService,
    ) { }

    ngOnInit(): void {
        const idParam = this.route.snapshot.paramMap.get('idContaFinanceira');
        this.idContaFinanceira = Number(idParam);

        const hoje = new Date();
        const ini = new Date(hoje.getFullYear(), hoje.getMonth() - 1, hoje.getDate());
        this.model = this.fb.group({
            dataDe: [ini, Validators.required],
            dataAte: [hoje, Validators.required],
        });

        this.carregarContas();
        this.carregarLancamentos(true);
        this.carregarLookups();
    }

    // ── Contas ────────────────────────────────────────────────────────────

    private carregarContas(): void {
        this.isLoadingContas = true;
        this.bancarioService.getContas().subscribe({
            next: res => {
                this.isLoadingContas = false;
                if (!res.isError) {
                    this._contas = res.contas;
                    this.carregarStatusContaAtual();
                }
            },
            error: () => { this.isLoadingContas = false; },
        });
    }

    private carregarStatusContaAtual(): void {
        // O config_extrato está vinculado à conta bancária, não ao cartão.
        // Usa o idContaFinanceiraVinculada (banco) para a verificação de status.
        const conta = this._contas.find(c => c.idContaFinanceira === this.idContaFinanceira);
        const idParaConsulta = conta?.idContaFinanceiraVinculada ?? this.idContaFinanceira;

        this.extratoService.getStatusExtratoContas([idParaConsulta]).subscribe({
            next: res => {
                const item = res.contas[0];
                this.statusConexaoExtrato = item ? (item.idStatusConexao as StatusConexaoEnum) : null;
            },
        });
    }

    get bancoDaContaSuportaOpenFinance(): boolean {
        const conta = this._contas.find(c => c.idContaFinanceira === this.idContaFinanceira);
        return !!conta?.suportaExtratoOpenFinance;
    }

    public irParaConfiguracaoIntegracao(): void {
        this.router.navigate(
            ['/configuracoes/financas/contas-financeiras/cadastrar', this.idContaFinanceira],
            { queryParams: { abrirIntegracao: true } }
        );
    }

    public onContaChange(idContaFinanceira: number): void {
        const conta = this._contas.find(c => c.idContaFinanceira === idContaFinanceira);
        if (conta?.idTipoConta !== TipoContaEnum.CartaoCredito) {
            this.router.navigate(
                ['/financas/conciliacao-bancaria/lancamentos', idContaFinanceira],
                { replaceUrl: true }
            );
            return;
        }
        this.idContaFinanceira = idContaFinanceira;
        this.router.navigate(
            ['/financas/conciliacao-bancaria/cartao/lancamentos', idContaFinanceira],
            { replaceUrl: true }
        );
        this.filtroNatureza = null;
        this.statusConexaoExtrato = null;
        this.carregarLancamentos(true);
        this.carregarStatusContaAtual();
    }

    public labelConta(conta: ExtratoBancarioContaItem): string {
        let label = `${conta.nomeBanco} — ${conta.descricao} (Ag. ${conta.agencia})`;
        if (conta.totalPendentes > 0) label += ` · ${conta.totalPendentes} pendente(s)`;
        return label;
    }

    // ── Lookups ───────────────────────────────────────────────────────────

    private carregarLookups(): void {
        this.financasLookupService.listCategoriaFinanceiraSelect().subscribe({
            next: res => { if (!res.isError) this.categoriasLookup = res.list; },
        });
        this.pessoaLookupService.listPessoaSelect().subscribe({
            next: res => { if (!res.isError) this.fornecedoresLookup = res.list; },
        });
    }

    // ── Period selector ───────────────────────────────────────────────────

    get periodoLabel(): string {
        switch (this.periodoSelecionado) {
            case PeriodoConciliacaoEnum.Hoje: return 'hoje';
            case PeriodoConciliacaoEnum.DaSemana: return 'desta semana';
            case PeriodoConciliacaoEnum.DoMes: return 'do mês';
            case PeriodoConciliacaoEnum.Ultimos30Dias: return 'últimos 30 dias';
            case PeriodoConciliacaoEnum.DoIntervalo: return 'do intervalo';
        }
    }

    public prepararPeriodoIntervalo(): void {
        this._periodoAnterior = this.periodoSelecionado;
    }

    public selecionarPeriodo(p: PeriodoConciliacaoEnum): void {
        this.periodoSelecionado = p;
    }

    public aplicarPeriodo(menuTrigger?: MatMenuTrigger): void {
        const hoje = new Date();
        let dataDe: Date;
        let dataAte: Date = hoje;

        switch (this.periodoSelecionado) {
            case PeriodoConciliacaoEnum.Hoje:
                dataDe = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
                dataAte = dataDe;
                break;
            case PeriodoConciliacaoEnum.DaSemana: {
                const dow = hoje.getDay();
                dataDe = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - dow);
                break;
            }
            case PeriodoConciliacaoEnum.DoMes:
                dataDe = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
                break;
            case PeriodoConciliacaoEnum.Ultimos30Dias:
                dataDe = new Date(hoje.getFullYear(), hoje.getMonth() - 1, hoje.getDate());
                break;
            case PeriodoConciliacaoEnum.DoIntervalo:
                if (this.model.invalid) {
                    this.model.markAllAsTouched();
                    return;
                }
                menuTrigger?.closeMenu();
                this.carregarLancamentos(true);
                return;
        }

        this.model.patchValue({ dataDe, dataAte });
        menuTrigger?.closeMenu();
        this.carregarLancamentos(true);
    }

    public cancelarPeriodoIntervalo(menuTrigger: MatMenuTrigger): void {
        this.periodoSelecionado = this._periodoAnterior;
        menuTrigger.closeMenu();
    }

    public onPeriodoMenuClosed(): void {
        if (this.periodoSelecionado === PeriodoConciliacaoEnum.DoIntervalo && this.model.invalid) {
            this.periodoSelecionado = this._periodoAnterior;
        }
    }

    public onDateInput(_event: Event): void { }

    // ── Loading + paginação ───────────────────────────────────────────────

    public carregarLancamentos(resetar = true): void {
        if (resetar) {
            if (this.isLoading) return;
            this._paginaAtual = 0;
            this._todosVm = [];
            this._temMais = false;
            this.alertForaPeriodo = null;
            this.isLoading = true;
        } else {
            if (this.isLoadingMais) return;
            this.isLoadingMais = true;
        }

        const dataDe: Date | null = this.model?.get('dataDe')?.value ?? null;
        const dataAte: Date | null = this.model?.get('dataAte')?.value ?? null;

        this.cartaoService.getLancamentos({
            idContaFinanceira: this.idContaFinanceira,
            dataInicio: dataDe?.toISOString() ?? null,
            dataFim: dataAte?.toISOString() ?? null,
            pageIndex: this._paginaAtual,
        }).subscribe({
            next: res => {
                this.isLoading = false;
                this.isLoadingMais = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao carregar lançamentos.', AlertType.error);
                    return;
                }
                if (resetar) {
                    this.descricaoContaFinanceira = res.descricaoContaFinanceira;
                    this.nomeBanco = res.nomeBanco;
                    this.agencia = res.agencia;
                    this.numeroConta = res.numeroConta;
                    if (res.pendentesForaPeriodo > 0 && res.dataMaisProximaPendente) {
                        const data = new Date(res.dataMaisProximaPendente);
                        this.alertForaPeriodo = {
                            quantidade: res.pendentesForaPeriodo,
                            mesLabel: this._formatarMes(data),
                            dataDe: new Date(data.getFullYear(), data.getMonth(), 1),
                            dataAte: new Date(data.getFullYear(), data.getMonth() + 1, 0),
                        };
                    }
                }
                const novos = (res.itens || []).map(item => ({
                    item,
                    sugestaoIdx: 0,
                    isConciliando: false,
                    isIgnorando: false,
                    acaoAberta: null as 'novaCp' | 'novaMov' | null,
                    formNovaCp: { idPessoaFornecedor: null as number | null, idCategoriaFinanceira: null as number | null, descricao: '' },
                    formNovaMov: { idCategoriaFinanceira: null as number | null, idPessoa: null as number | null, descricao: '' },
                }));
                this._todosVm = [...this._todosVm, ...novos];
                this._temMais = res.temMais;
                this.aplicarFiltros();
            },
            error: () => {
                this.isLoading = false;
                this.isLoadingMais = false;
                this.alertService.show('Erro', 'Erro ao comunicar com o servidor.', AlertType.error);
            },
        });
    }

    public proximaPagina(): void {
        this._paginaAtual++;
        this.carregarLancamentos(false);
    }

    // ── Filters ───────────────────────────────────────────────────────────

    public aplicarFiltros(): void {
        if (this.filtroNatureza === null) {
            this._filtradosVm = [...this._todosVm];
        } else {
            this._filtradosVm = this._todosVm.filter(vm => vm.item.idNaturezaMovimentacao === this.filtroNatureza);
        }
    }

    get totalTodos(): number { return this._todosVm.length; }
    get totalCompras(): number {
        return this._todosVm.filter(vm => vm.item.idNaturezaMovimentacao === NaturezaMovimentacaoEnum.Saida).length;
    }
    get totalEstornos(): number {
        return this._todosVm.filter(vm => vm.item.idNaturezaMovimentacao === NaturezaMovimentacaoEnum.Entrada).length;
    }

    public setFiltro(natureza: number | null): void {
        this.filtroNatureza = natureza;
        this.aplicarFiltros();
    }

    // ── Match / Sugestões ─────────────────────────────────────────────────

    public getSugestaoAtual(vm: LancamentoCartaoVm): SugestaoMatch | null {
        if (!vm.item.sugestoes?.length || vm.sugestaoIdx < 0) return null;
        return vm.item.sugestoes[vm.sugestaoIdx] ?? null;
    }

    public trocarSugestao(vm: LancamentoCartaoVm): void {
        if (!vm.item.sugestoes?.length) return;
        vm.sugestaoIdx = (vm.sugestaoIdx + 1) % vm.item.sugestoes.length;
    }

    public ignorarSugestao(vm: LancamentoCartaoVm): void {
        vm.sugestaoIdx = -1;
    }

    public tipoMatchLabel(tipoMatch: number): string {
        switch (tipoMatch) {
            case TipoMatchConciliacaoEnum.ContaPagar: return 'Conta a Pagar';
            case TipoMatchConciliacaoEnum.Movimentacao: return 'Movimentação';
            default: return '';
        }
    }

    public stripHtml(html: string): string {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    }

    // ── Actions ───────────────────────────────────────────────────────────

    public conciliarMatch(vm: LancamentoCartaoVm): void {
        const s = this.getSugestaoAtual(vm);
        if (!s) return;

        vm.isConciliando = true;

        const request: ConciliarCartaoCreditoRequest =
            vm.item.idNaturezaMovimentacao === NaturezaMovimentacaoEnum.Saida
                ? { idExtratoBancario: vm.item.idExtratoBancario, modo: ModoConciliacaoCartaoEnum.AssociarCp, idContasPagarParcela: s.idRegistro }
                : { idExtratoBancario: vm.item.idExtratoBancario, modo: ModoConciliacaoCartaoEnum.AssociarMovimentacao, idMovimentacao: s.idRegistro };

        this.cartaoService.conciliar(request).subscribe({
            next: res => {
                vm.isConciliando = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Falha ao conciliar.', AlertType.error);
                    return;
                }
                this.alertService.show('Sucesso', 'Conciliação realizada.', AlertType.success);
                this._removerDaLista(vm);
            },
            error: () => {
                vm.isConciliando = false;
                this.alertService.show('Erro', 'Erro ao comunicar com o servidor.', AlertType.error);
            },
        });
    }

    public abrirConciliar(vm: LancamentoCartaoVm): void {
        const data: ConciliarCartaoDialogData = {
            idExtratoBancario: vm.item.idExtratoBancario,
            idNaturezaMovimentacao: vm.item.idNaturezaMovimentacao,
            dataTransacao: vm.item.dataTransacao,
            valor: vm.item.valor,
            memo: vm.item.memo ?? null,
        };

        const ref = this.dialog.open(ConciliarCartaoDialogComponent, {
            data,
            disableClose: false,
            width: '90vw',
            maxWidth: '1200px',
            maxHeight: '90vh',
        });

        ref.afterClosed().subscribe(result => {
            if (result?.conciliado) this._removerDaLista(vm);
        });
    }

    public ignorar(vm: LancamentoCartaoVm): void {
        vm.isIgnorando = true;
        this.cartaoService.ignorar(vm.item.idExtratoBancario).subscribe({
            next: res => {
                vm.isIgnorando = false;
                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription || 'Erro ao ignorar.', AlertType.warning);
                    return;
                }
                this.alertService.show('Sucesso', 'Lançamento ignorado.', AlertType.success);
                this._removerDaLista(vm);
            },
            error: () => {
                vm.isIgnorando = false;
                this.alertService.show('Erro', 'Erro ao comunicar com o servidor.', AlertType.error);
            },
        });
    }

    private _removerDaLista(vm: LancamentoCartaoVm): void {
        this._todosVm = this._todosVm.filter(v => v !== vm);
        this.aplicarFiltros();
    }

    // ── Inline forms ──────────────────────────────────────────────────────

    public abrirNovaCp(vm: LancamentoCartaoVm): void {
        vm.acaoAberta = 'novaCp';
        vm.formNovaCp = { idPessoaFornecedor: null, idCategoriaFinanceira: null, descricao: vm.item.memo || '' };
    }

    public abrirNovaMov(vm: LancamentoCartaoVm): void {
        vm.acaoAberta = 'novaMov';
        vm.formNovaMov = { idCategoriaFinanceira: null, idPessoa: null, descricao: vm.item.memo || '' };
    }

    public fecharAcao(vm: LancamentoCartaoVm): void {
        vm.acaoAberta = null;
    }

    public confirmarNovaCp(vm: LancamentoCartaoVm): void {
        if (vm.isConciliando) return;
        vm.isConciliando = true;
        const request: ConciliarCartaoCreditoRequest = {
            idExtratoBancario: vm.item.idExtratoBancario,
            modo: ModoConciliacaoCartaoEnum.CriarCp,
            dadosCp: {
                idPessoaFornecedor: vm.formNovaCp.idPessoaFornecedor!,
                idCategoriaFinanceira: vm.formNovaCp.idCategoriaFinanceira!,
                descricao: vm.formNovaCp.descricao || vm.item.memo || 'Compra cartão',
            },
        };
        this.cartaoService.conciliar(request).subscribe({
            next: res => {
                vm.isConciliando = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Falha ao criar CP.', AlertType.error);
                    return;
                }
                this.alertService.show('Sucesso', 'Conta a Pagar criada e conciliada.', AlertType.success);
                this._removerDaLista(vm);
            },
            error: () => {
                vm.isConciliando = false;
                this.alertService.show('Erro', 'Erro ao comunicar com o servidor.', AlertType.error);
            },
        });
    }

    public confirmarNovaMov(vm: LancamentoCartaoVm): void {
        if (vm.isConciliando) return;
        vm.isConciliando = true;
        const request: ConciliarCartaoCreditoRequest = {
            idExtratoBancario: vm.item.idExtratoBancario,
            modo: ModoConciliacaoCartaoEnum.CriarMovimentacao,
            dadosMov: {
                idCategoriaFinanceira: vm.formNovaMov.idCategoriaFinanceira ?? null,
                idPessoa: vm.formNovaMov.idPessoa ?? null,
                descricao: vm.formNovaMov.descricao || vm.item.memo || 'Estorno cartão',
            },
        };
        this.cartaoService.conciliar(request).subscribe({
            next: res => {
                vm.isConciliando = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Falha ao criar movimentação.', AlertType.error);
                    return;
                }
                this.alertService.show('Sucesso', 'Movimentação criada e conciliada.', AlertType.success);
                this._removerDaLista(vm);
            },
            error: () => {
                vm.isConciliando = false;
                this.alertService.show('Erro', 'Erro ao comunicar com o servidor.', AlertType.error);
            },
        });
    }

    // ── Navegação ─────────────────────────────────────────────────────────

    public irParaConciliados(): void {
        this.router.navigate(['/financas/conciliacao-bancaria/cartao/conciliados', this.idContaFinanceira]);
    }

    public irParaIgnorados(): void {
        this.router.navigate(['/financas/conciliacao-bancaria/ignorados', this.idContaFinanceira], {
            queryParams: { origem: 'cartao' },
        });
    }

    public navegarParaMesPendente(): void {
        if (!this.alertForaPeriodo) return;
        this.periodoSelecionado = PeriodoConciliacaoEnum.DoIntervalo;
        this.model.patchValue({
            dataDe: this.alertForaPeriodo.dataDe,
            dataAte: this.alertForaPeriodo.dataAte,
        });
        this.alertForaPeriodo = null;
        this.carregarLancamentos(true);
    }

    private _formatarMes(data: Date): string {
        const meses = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho',
            'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
        return `${meses[data.getMonth()]}/${data.getFullYear()}`;
    }

    public voltar(): void {
        this.router.navigate(['/financas/conciliacao-bancaria']);
    }
}
